"use client"

import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Plus, X } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

/**
 * Small reusable editors for the list/dictionary shapes that repeat across the
 * vendor configuration's status mappings and business rules.
 */

function ListEditor({
  values,
  onChange,
  placeholder,
  numeric,
}: {
  values: unknown[]
  onChange: (values: any[]) => void
  placeholder?: string
  numeric?: boolean
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState("")

  // Values come from an admin-pasted document (JSON tab) and may not be an array
  // of the expected type — a render crash here would lose every unsaved edit in
  // the parent editor, so everything is coerced defensively
  const safeValues = Array.isArray(values) ? values : []

  const add = () => {
    const raw = draft.trim()
    if (!raw) return
    const value: string | number = numeric ? Number(raw) : raw
    if (numeric && !Number.isFinite(value as number)) return
    const isDuplicate = numeric
      ? safeValues.includes(value)
      : safeValues.some((v) => String(v).toLowerCase() === raw.toLowerCase())
    if (!isDuplicate) {
      onChange([...safeValues, value])
    }
    setDraft("")
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {safeValues.map((value, index) => (
          <span
            key={`${String(value)}-${index}`}
            className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs"
          >
            {String(value)}
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => onChange(safeValues.filter((_, i) => i !== index))}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {safeValues.length === 0 && (
          <span className="text-xs text-muted-foreground">{t("common.state.none")}</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          type={numeric ? "number" : "text"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              add()
            }
          }}
          placeholder={placeholder}
          className={numeric ? "h-8 text-xs w-40" : "h-8 text-xs"}
        />
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={!draft.trim()}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}

export function StringListEditor(props: {
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
}) {
  return <ListEditor {...props} />
}

export function NumberListEditor(props: {
  values: number[]
  onChange: (values: number[]) => void
  placeholder?: string
}) {
  return <ListEditor {...props} numeric />
}

/** Dictionary editor: string keys to string or numeric values. */
export function KeyValueListEditor({
  entries,
  onChange,
  keyPlaceholder,
  valuePlaceholder,
  numericValues,
}: {
  entries: Record<string, string | number>
  onChange: (entries: Record<string, string | number>) => void
  keyPlaceholder?: string
  valuePlaceholder?: string
  numericValues?: boolean
}) {
  const { t } = useTranslation()
  const [draftKey, setDraftKey] = useState("")
  const [draftValue, setDraftValue] = useState("")

  // Same defensive normalization as ListEditor — pasted JSON can hold anything here
  const safeEntries =
    entries && typeof entries === "object" && !Array.isArray(entries) ? entries : ({} as Record<string, string | number>)
  const pairs = Object.entries(safeEntries)

  const add = () => {
    const key = draftKey.trim()
    const rawValue = draftValue.trim()
    if (!key || !rawValue) return
    const value = numericValues ? Number(rawValue) : rawValue
    if (numericValues && !Number.isFinite(value as number)) return
    onChange({ ...safeEntries, [key]: value })
    setDraftKey("")
    setDraftValue("")
  }

  const remove = (key: string) => {
    const next = { ...safeEntries }
    delete next[key]
    onChange(next)
  }

  return (
    <div className="space-y-2">
      {pairs.length > 0 ? (
        <div className="space-y-1">
          {pairs.map(([key, value]) => (
            <div key={key} className="flex items-center gap-2 text-xs">
              <span className="font-mono bg-muted rounded px-1.5 py-0.5">{key}</span>
              <span className="text-muted-foreground">→</span>
              <span className="font-mono">{String(value)}</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground ml-auto"
                onClick={() => remove(key)}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t("eventSync.lists.noEntries")}</p>
      )}
      <div className="flex gap-2">
        <Input
          value={draftKey}
          onChange={(e) => setDraftKey(e.target.value)}
          placeholder={keyPlaceholder ?? t("eventSync.lists.keyPlaceholder")}
          className="h-8 text-xs"
        />
        <Input
          type={numericValues ? "number" : "text"}
          value={draftValue}
          onChange={(e) => setDraftValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              add()
            }
          }}
          placeholder={valuePlaceholder ?? t("eventSync.lists.valuePlaceholder")}
          className="h-8 text-xs"
        />
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={!draftKey.trim() || !draftValue.trim()}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}
